import { GIFT_CODE_TYPE_LABELS, type GiftCodeStatus } from "@loot-os/api-contract";
// Type-only import: the dashboard never bundles server code.
import type { AppRouter } from "@loot-os/functions/src/dashboard-api/trpc/router.js";
import type { inferOutput, TRPCOptionsProxy } from "@trpc/tanstack-react-query";

import { formatShortDate } from "./format";

type BatchOutput = inferOutput<TRPCOptionsProxy<AppRouter>["giftCodes"]["batch"]>;

/** A batch as `giftCodes.batches` and `giftCodes.batch` return it. */
export type GiftCodeBatch = BatchOutput["batch"];

/** One code of `giftCodes.batch`, printed with its dashes. */
export type GiftCodeRow = BatchOutput["codes"][number];

/** A batch at a glance: voided, every code redeemed, past its expiry, or still redeemable. */
export type GiftCodeBatchState = "voided" | "redeemed" | "expired" | "active";

export function giftCodeBatchState(
  batch: Pick<GiftCodeBatch, "voidedAt" | "expiresAt" | "codeCount" | "redeemedCount">,
  now: Date,
): GiftCodeBatchState {
  if (batch.voidedAt) return "voided";
  if (batch.redeemedCount >= batch.codeCount) return "redeemed";
  if (batch.expiresAt && new Date(batch.expiresAt) <= now) return "expired";
  return "active";
}

/** Badge variants for a code's or a batch's state: only a redeemable one is filled. */
export const GIFT_CODE_STATE_BADGE: Record<
  GiftCodeStatus | GiftCodeBatchState,
  "default" | "secondary" | "outline" | "destructive"
> = {
  active: "default",
  redeemed: "secondary",
  expired: "outline",
  voided: "destructive",
};

/** How a batch is named in the breadcrumb and the CSV: "Giveaway · Sep 30, 2026". */
export function giftCodeBatchLabel(batch: Pick<GiftCodeBatch, "type" | "createdAt">): string {
  return `${GIFT_CODE_TYPE_LABELS[batch.type]} · ${formatShortDate(batch.createdAt)}`;
}

/** The batch's CSV name without `.csv`: `gift-codes-giveaway-2026-09-30`. */
export function giftCodesCsvName(batch: Pick<GiftCodeBatch, "type" | "createdAt">): string {
  return `gift-codes-${batch.type}-${batch.createdAt.slice(0, 10)}`;
}

/** The last moment of a picked day in local time: a code expiring "Oct 31" works all that day. */
export function endOfLocalDay(day: Date): Date {
  return new Date(day.getFullYear(), day.getMonth(), day.getDate(), 23, 59, 59, 999);
}
